import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Controller, useFormContext } from 'react-hook-form';

import { useStyles } from '../styles';
import { AddAgentPromptFormValue } from '../types';
import { MarkdownEditor } from '@shared/components/MarkdownEditor';
import { BorderedGrid } from '@shared/components/borderedGrid';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';

interface AddAgentProfileFormProps {
  template?: AddAgentPromptFormValue;
}

export const AddAgentProfileForm: React.FC<AddAgentProfileFormProps> = ({ template }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [openingLineExpanded, setOpeningLineExpanded] = useState(false);
  const {
    setValue,
    control,
    formState: { errors },
  } = useFormContext<AddAgentPromptFormValue>();

  useEffect(() => {
    if (!template) return;

    setValue('name', template.name);
    setValue('prompt', template.prompt);
    setValue('firstMessage', template.firstMessage ?? '');
    setValue(
      'firstMessageInterruptionsEnabled',
      template.firstMessageInterruptionsEnabled ?? false,
    );
    setOpeningLineExpanded(template.firstMessageInterruptionsEnabled ?? false);
  }, [setValue, template]);

  return (
    <>
      <Box className={classes.formField}>
        <Controller
          name="name"
          control={control}
          rules={{ required: t('assistantsPage.modals.validation.name') }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label={t('assistantsPage.modals.add.nameLabel')}
              error={!!errors.name}
              helperText={errors.name?.message}
              variant="outlined"
              className={classes.filledField}
            />
          )}
        />
      </Box>
      <BorderedGrid sx={{ p: 0, overflow: 'hidden' }}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={openingLineExpanded}
          onChange={(_, expanded) => setOpeningLineExpanded(expanded)}
          sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="agent-profile-opening-line-content"
            id="agent-profile-opening-line-header"
            sx={{ minHeight: 72, px: 2.5, '& .MuiAccordionSummary-content': { mr: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Typography fontSize="16px" fontWeight={600}>
                {t('assistantsPage.modals.openingLine.label')}
              </Typography>
              <FormGroup>
                <Controller
                  name="firstMessageInterruptionsEnabled"
                  control={control}
                  render={({ field: switchField }) => (
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      onClick={(event) => event.stopPropagation()}
                      control={
                        <CustomSwitch
                          checked={!!switchField.value}
                          inputProps={{
                            'aria-label': t(
                              'assistantsPage.modals.firstMessageInterruptionsEnabled.label',
                            ),
                          }}
                          onClick={(event: { stopPropagation: () => any }) =>
                            event.stopPropagation()
                          }
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const enabled = e.target.checked;

                            switchField.onChange(enabled);
                            setOpeningLineExpanded(enabled);
                          }}
                        />
                      }
                      label=""
                    />
                  )}
                />
              </FormGroup>
            </Stack>
          </AccordionSummary>
          <AccordionDetails
            id="agent-profile-opening-line-content"
            sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
            <Controller
              name="firstMessage"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder={t('assistantsPage.modals.openingLine.placeholder')}
                  variant="outlined"
                  className={classes.filledField}
                />
              )}
            />
          </AccordionDetails>
        </Accordion>
      </BorderedGrid>
      <Box className={classes.formField}>
        <Controller
          name="prompt"
          control={control}
          rules={{ required: t('assistantsPage.modals.validation.prompt') }}
          render={({ field }) => {
            return (
              <Box>
                <MarkdownEditor
                  value={field.value}
                  onChange={field.onChange}
                  label={t('assistantsPage.modals.add.promptLabel')}
                  error={!!errors.prompt}
                  helperText={errors.prompt?.message}
                  required
                  height={500}
                />
              </Box>
            );
          }}
        />
      </Box>
    </>
  );
};
